import React, { useCallback, useMemo } from 'react'
import {
  Control,
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form'
import CheckboxGroup, { CheckboxGroupProps } from './CheckboxGroup'

export type BaseFormRadioGroupProps<T extends FieldValues = FieldValues> = {
  name: Path<T>
  control: Control<T>
} & Omit<CheckboxGroupProps, 'active' | 'setActive' | 'mode'>

export type FormRadioGroupWithContextProps<
  T extends FieldValues = FieldValues
> = Omit<BaseFormRadioGroupProps<T>, 'control'>

const BaseFormRadioGroup = <T extends FieldValues = FieldValues>({
  name,
  control,
  ...props
}: BaseFormRadioGroupProps<T>) => {
  const { field, fieldState } = useController({
    name,
    control,
  })

  const { error } = fieldState

  //   const { i18n } = useTranslation(['validation'])

  const setActive = useCallback(
    (update: string[] | ((prevState: string[]) => string[])) => {
      if (typeof update === 'function') {
        const newActive = update(field.value)
        field.onChange(newActive[0])
        return
      }

      field.onChange(update[0])
    },
    [field]
  )

  const active = useMemo(() => {
    if (Array.isArray(field.value)) {
      return field.value
    }
    // Make sure we don't return [undefined] or [null]
    if (field.value != null) {
      return [field.value]
    }

    return []
  }, [field.value])

  return (
    <CheckboxGroup
      {...props}
      mode="radio"
      active={active}
      setActive={setActive}
      error={!!error?.message}
    />
  )
}

export const FormInputWithContext = <T extends FieldValues = FieldValues>({
  ...props
}: FormRadioGroupWithContextProps<T>) => {
  const { control } = useFormContext<T>()

  return <BaseFormRadioGroup {...props} control={control} />
}

export type FormInputProps<T extends FieldValues> = {
  control?: Control<T>
} & FormRadioGroupWithContextProps<T>

const FormRadioGroup = <T extends FieldValues>(props: FormInputProps<T>) => {
  const { control } = props

  if (!control) {
    return <FormInputWithContext {...props} />
  }

  return <BaseFormRadioGroup {...props} control={control} />
}

export default FormRadioGroup

FormRadioGroup.Item = CheckboxGroup.Item
